/**
 * Replication between a node and its cloud (PLAN §6.5, §6.6; wave 4b-1): the
 * REST half of the site link. The WebSocket half (`siteLink.ts`) carries the
 * live, small frames — `rows.state` fan-out, hints, playout commands and
 * states; everything bulky or needing a synchronous answer goes here, as
 * signed node requests (`auth: 'node'`, `@acp/cloud-link/nodeSignature.js`):
 *
 *   GET  /node/sync                               the site's records and the partitions it replicates
 *   GET  /node/rundowns/{rundownId}/rows          `changedSince` catch-up, cloud → node (paged)
 *   POST /node/rundowns/{rundownId}/rows          the node's outbox, drained as canonical rows, node → cloud
 *   PUT  /node/productions/{productionId}         a production record the node wrote (created, activated, ended…)
 *   POST /node/productions/{productionId}/asrun  as-run events, append-only and idempotent
 *   PUT  /node/productions/{productionId}/playout a playout snapshot too large for one site-link frame
 *
 * WHY REST FOR THESE. API Gateway's WebSocket frames are at most 128 KB and a
 * Lambda cannot answer a frame synchronously; a catch-up page, a studio config
 * (up to ~200 KB) or a long as-run page would not fit, and the outbox needs an
 * acknowledgement it can delete its entries on. A signed request is both
 * size-safe (the router's 256 KB body; responses up to Lambda's 6 MB) and
 * acknowledged.
 *
 * THE SCOPE (enforced by the cloud, never trusted from the node): a node
 * replicates only its own tenant's data (the tenant is the signature's), and
 * of it only its SITE's partitions — every playlist and rundown bound to one of
 * the site's studios (`studioId` on the head row), the rundowns of a bound
 * playlist, and every playlist or rundown a production of those studios runs
 * (with a playlist's rundowns). `GET /node/sync` answers that list; every
 * other route refuses a partition outside it (`not-found`, the same as one
 * that does not exist). Studios and productions: the site's studios and their
 * productions; a node may write only productions of its site's studios. Show
 * styles (D32): those the site's studios name as their default and those its
 * productions run — read-only on the node, like studios.
 *
 * Idempotent throughout: rows merge per field (`mergeRows`, D6), a production
 * record is kept by the higher `version`, as-run events by `eventId`.
 */
import { rowWireSchema } from './rowDocument.js';
import { ulidSchema } from '@acp/domain/primitives.js';
import { z } from 'zod';
import { asRunEventWireSchema, playoutStateWireSchema } from './playout.js';
import { productionPathWireSchema, productionWireSchema } from './productions.js';
import { cursorWireSchema, pageLimitWireSchema, pageTokenWireSchema, PAGE_LIMIT_DESCRIPTION, PAGE_TOKEN_DESCRIPTION, rundownPathWireSchema } from './rows.js';
import type { RouteDef } from './routes.js';
import { siteNodeRoleOpenWireSchema } from './sites.js';
import { showStyleWireSchema } from './showStyles.js';
import { studioWireSchema } from './studios.js';

/** The most rows one push carries: the node splits its outbox into requests of at most this many (and under the body limit). */
export const MAX_PUSH_ROWS = 200;

/** The most as-run events one push carries. */
export const MAX_PUSH_ASRUN = 500;

/** A partition a site replicates: a playlist's or a rundown's (their ids are the partition ids). */
export const replicatedPartitionWireSchema = z.object({ id: ulidSchema, kind: z.enum(['playlist', 'rundown']) }).strict();
export type ReplicatedPartitionWire = z.infer<typeof replicatedPartitionWireSchema>;

/** A node of the site as its peers and the SPA need it (the `/config.json` node list, PLAN §10.2). */
export const syncNodeWireSchema = z
  .object({
    nodeId: z.string().min(1).max(64),
    role: siteNodeRoleOpenWireSchema,
    label: z.string().max(128),
    /** Its local HTTPS name (D16), or null before it has one. */
    dnsName: z.string().nullable(),
  })
  .strict();
export type SyncNodeWire = z.infer<typeof syncNodeWireSchema>;

export const nodeSyncResponseWireSchema = z
  .object({
    site: z.object({ siteId: z.string().min(1).max(64), name: z.string().max(200) }).strict(),
    /** The site's active nodes, the caller included. */
    nodes: z.array(syncNodeWireSchema),
    /** The site's studios, with their configs. */
    studios: z.array(studioWireSchema),
    /**
     * The show styles the site uses (D32), with their configs: every studio's `defaultShowStyleId` and every
     * listed production's `showStyleId`. Absent from a pre-D32 cloud: none.
     */
    showStyles: z.array(showStyleWireSchema).default([]),
    /** Every production of the site's studios that has not ended, and those ended within the last day. */
    productions: z.array(productionWireSchema),
    /** What the node replicates (the module comment's rule). */
    partitions: z.array(replicatedPartitionWireSchema),
  })
  .strict();
export type NodeSyncResponseWire = z.infer<typeof nodeSyncResponseWireSchema>;

export const nodeRowsQueryWireSchema = z
  .object({
    changedSince: cursorWireSchema.optional().describe('Catch-up: only rows changed since this cursor (the previous listing’s `cursor`; opaque).'),
    pageToken: pageTokenWireSchema.optional().describe(PAGE_TOKEN_DESCRIPTION),
    limit: pageLimitWireSchema.optional().describe(PAGE_LIMIT_DESCRIPTION),
  })
  .strict();

export const nodeRowsResponseWireSchema = z
  .object({
    rows: z.array(rowWireSchema),
    nextPageToken: pageTokenWireSchema.nullable(),
    /** The partition's high-water mark when the listing started: the next `changedSince`. */
    cursor: cursorWireSchema,
  })
  .strict();

export const pushRowsRequestWireSchema = z.object({ rows: z.array(rowWireSchema).min(1).max(MAX_PUSH_ROWS) }).strict();
export type PushRowsRequestWire = z.infer<typeof pushRowsRequestWireSchema>;

export const pushRowsResponseWireSchema = z
  .object({
    /** How many rows changed the cloud's copy (the rest were already known: a retry, or an older state). */
    changed: z.number().int().nonnegative(),
  })
  .strict();

export const putProductionRequestWireSchema = z.object({ production: productionWireSchema }).strict();

export const putProductionResponseWireSchema = z
  .object({
    /** The record the cloud now holds: the node's when its `version` was not older, else the cloud's own. */
    production: productionWireSchema,
    stored: z.boolean(),
  })
  .strict();

export const pushAsRunRequestWireSchema = z.object({ events: z.array(asRunEventWireSchema).min(1).max(MAX_PUSH_ASRUN) }).strict();
export const pushAsRunResponseWireSchema = z.object({ appended: z.number().int().nonnegative() }).strict();

export const putPlayoutSnapshotRequestWireSchema = z.object({ state: playoutStateWireSchema }).strict();
export const putPlayoutSnapshotResponseWireSchema = z.object({ stored: z.boolean() }).strict();

const NODE_ROUTE = { permission: null, permissionScope: 'tenant', auth: 'node', tag: 'node' } as const;

export const NODE_SYNC_ROUTES = {
  getNodeSync: {
    ...NODE_ROUTE,
    id: 'getNodeSync',
    method: 'GET',
    path: '/node/sync',
    summary: 'The site’s records, for the calling node',
    description:
      'What the calling node replicates (wave 4b-1, PLAN §6.5–6.6): its site and active nodes, the site’s studios with their configs, the show styles they or their productions use (D32), their productions (not ended, or ended within a day), and the partitions — playlists and rundowns — the site replicates: those bound to its studios (`studioId` on the head row), a bound playlist’s rundowns, and whatever its productions run. Also (re)subscribes the node’s site link to those partitions’ live `rows.state` frames. A node calls it after every (re)connect and on a `records.changed` hint.',
    params: null,
    query: null,
    body: null,
    response: nodeSyncResponseWireSchema,
    successStatus: 200,
    errors: [],
  },
  listNodeRows: {
    ...NODE_ROUTE,
    id: 'listNodeRows',
    method: 'GET',
    path: '/node/rundowns/{rundownId}/rows',
    summary: 'Catch up a replicated partition',
    description:
      'The rows of one of the site’s partitions (`GET /node/sync`’s list), all of them or changed since a cursor — the same listing as `GET /rundowns/{rundownId}/rows`, for a node. A partition outside the site’s scope is `not-found`.',
    params: rundownPathWireSchema,
    query: nodeRowsQueryWireSchema,
    body: null,
    response: nodeRowsResponseWireSchema,
    successStatus: 200,
    errors: ['bad-request', 'not-found'],
  },
  pushNodeRows: {
    ...NODE_ROUTE,
    id: 'pushNodeRows',
    method: 'POST',
    path: '/node/rundowns/{rundownId}/rows',
    summary: 'Push rows the node changed',
    description:
      'The node’s outbox, drained: the canonical rows it changed, merged into the cloud’s copies per field (`mergeRows`: LWW, Yjs for text — idempotent and commutative, so a retry or a reordering is harmless) and fanned out to the cloud’s subscribers and the other site links. A partition outside the site’s scope is `not-found`; a row of another partition is `validation-failed`.',
    params: rundownPathWireSchema,
    query: null,
    body: pushRowsRequestWireSchema,
    response: pushRowsResponseWireSchema,
    successStatus: 200,
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  putNodeProduction: {
    ...NODE_ROUTE,
    id: 'putNodeProduction',
    method: 'PUT',
    path: '/node/productions/{productionId}',
    summary: 'Store a production record the node wrote',
    description:
      'A production of one of the site’s studios, as the node holds it (created on the node, or activated, ended or pinned there). Kept when its `version` is at least the cloud’s (the node runs the production, so a tie goes to the node’s copy); an older one is answered with the cloud’s. A production of another site’s studio is `not-found`.',
    params: productionPathWireSchema,
    query: null,
    body: putProductionRequestWireSchema,
    response: putProductionResponseWireSchema,
    successStatus: 200,
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  pushNodeAsRun: {
    ...NODE_ROUTE,
    id: 'pushNodeAsRun',
    method: 'POST',
    path: '/node/productions/{productionId}/asrun',
    summary: 'Append as-run events from the node',
    description:
      'The production’s as-run events (PLAN §5.4, §6.6), append-only and at-least-once: an event whose id the cloud holds is kept as first written. A production of another site’s studio is `not-found`.',
    params: productionPathWireSchema,
    query: null,
    body: pushAsRunRequestWireSchema,
    response: pushAsRunResponseWireSchema,
    successStatus: 200,
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
  putNodePlayout: {
    ...NODE_ROUTE,
    id: 'putNodePlayout',
    method: 'PUT',
    path: '/node/productions/{productionId}/playout',
    summary: 'Store a playout snapshot',
    description:
      'The production’s playout state, when it is too large for one site-link `playout.state` frame (128 KB): kept as the cloud’s last snapshot (served read-only while the node is offline) unless the cloud already holds a later `seq`, and published to the cloud’s subscribers.',
    params: productionPathWireSchema,
    query: null,
    body: putPlayoutSnapshotRequestWireSchema,
    response: putPlayoutSnapshotResponseWireSchema,
    successStatus: 200,
    errors: ['bad-request', 'validation-failed', 'not-found', 'payload-too-large'],
  },
} as const satisfies Record<string, RouteDef>;
